import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

/**
 * Files API using RTK Query
 *
 * Provides file management endpoints with automatic caching,
 * request deduplication, and loading state management.
 *
 * Uses centralized baseQuery for consistent error handling.
 */
export const filesApi = createApi({
  reducerPath: 'filesApi',
  baseQuery,
  tagTypes: ['Files'],
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    /**
     * Get list of files
     * @param {Object} params - Query parameters
     * @param {string} [params.userId] - Filter by user ID
     * @param {string} [params.segmentedKnowledgeId] - Filter by segmented knowledge ID
     * @param {string} [params.folderId] - Filter by folder ID (null for root)
     */
    getFiles: builder.query({
      query: ({ userId, segmentedKnowledgeId, folderId } = {}) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (segmentedKnowledgeId) params.append('segmented_knowledge_id', segmentedKnowledgeId);
        if (folderId !== undefined) {
          // Support filtering by folder: pass folder ID or 'none' for null folder (root)
          params.append('folder_id', folderId === null ? 'none' : folderId);
        }
        const queryString = params.toString();
        return `/api/v1/files/${queryString ? `?${queryString}` : ''}`;
      },
      // LIST/ITEM pattern: provide both individual item tags and list tag
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Files', id })),
              { type: 'Files', id: 'LIST' },
            ]
          : [{ type: 'Files', id: 'LIST' }],
    }),

    /**
     * Get presigned S3 download URL
     */
    getDownloadUrl: builder.query({
      query: (fileId) => `/api/v1/files/${fileId}/get_download_url/`,
    }),

    /**
     * Delete file
     */
    deleteFile: builder.mutation({
      query: (fileId) => ({
        url: `/api/v1/files/${fileId}/`,
        method: 'DELETE',
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, fileId) => [
        { type: 'Files', id: fileId },
        { type: 'Files', id: 'LIST' },
      ],
    }),

    /**
     * Move file to folder
     * @param {Object} params - Move parameters
     * @param {string} params.fileId - File ID to move
     * @param {string|null} params.folderId - Target folder ID (null to move to root)
     */
    moveFileToFolder: builder.mutation({
      query: ({ fileId, folderId }) => ({
        url: `/api/v1/files/${fileId}/move-to-folder/`,
        method: 'PATCH',
        body: { folder_id: folderId },
      }),
      // Invalidate both the specific file and the list (affects folder organization)
      invalidatesTags: (result, error, { fileId }) => [
        { type: 'Files', id: fileId },
        { type: 'Files', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetFilesQuery,
  useLazyGetDownloadUrlQuery,
  useDeleteFileMutation,
  useMoveFileToFolderMutation,
} = filesApi;
